import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useId, useRef} from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {type ToolRequestedInput} from "../types.ts";

import {RequestedInputLabel} from "./requested-input-label.tsx";

import infoIcon from "@phosphor-icons/core/regular/info.svg";

interface Props {
    label: string;
    description?: string;
    variableName: string;
    options: ToolRequestedInput["selectOptions"];
    required?: boolean;
    disabled?: boolean;
    values: Record<string, string>;
    setValues: (value: React.SetStateAction<Record<string, string>>) => void;
    error?: string | null;
}

export const RequestedInputGridSelect = ({
    label,
    description,
    variableName,
    options = [],
    required,
    disabled,
    values,
    setValues,
    error,
}: Props): React.ReactElement => {
    const id = useId();
    const ref = useRef<HTMLDivElement>(null);

    const selectedValue = values[variableName];

    const currentOptions =
        typeof options === "function" ? options(values) : options;

    useEffect(() => {
        if (
            selectedValue &&
            !currentOptions.some((o) => o.value === selectedValue)
        ) {
            // If the currently-selected value isn't one of the available
            // options, clear the selection:
            setValues((prev) => {
                delete prev[variableName];
                return {...prev};
            });
        }
    }, [currentOptions, selectedValue, variableName, setValues]);

    const selectOption = useCallback(
        (index: number) => {
            setValues((prev) => ({
                ...prev,
                [variableName]: currentOptions[index].value,
            }));

            // NOTE(reid): nth-child uses 1-based indexing, so we add 1 here
            const optionElement = ref.current!.querySelector<HTMLElement>(
                `[role=radio]:nth-child(${index + 1})`,
            );
            if (optionElement) {
                optionElement.focus();
            }
        },
        [setValues, currentOptions, variableName],
    );

    const handleKeyDown = useCallback(
        (evt: React.KeyboardEvent) => {
            if (evt.key === "ArrowDown" || evt.key === "ArrowRight") {
                evt.preventDefault();
                const selectedIndex = currentOptions.findIndex(
                    (o) => o.value === selectedValue,
                );
                const newSelectedIndex =
                    (selectedIndex + 1) % currentOptions.length;
                selectOption(newSelectedIndex);
            } else if (evt.key === "ArrowUp" || evt.key === "ArrowLeft") {
                evt.preventDefault();
                const selectedIndex = currentOptions.findIndex(
                    (o) => o.value === selectedValue,
                );
                const newSelectedIndex =
                    selectedIndex === 0
                        ? currentOptions.length - 1
                        : selectedIndex - 1;
                selectOption(newSelectedIndex);
            }
        },
        [currentOptions, selectedValue, selectOption],
    );

    return (
        <RequestedInputLabel
            id={id}
            label={label}
            description={description}
            required={required}
            error={error}
        >
            <View
                style={styles.container}
                id={id}
                role="radiogroup"
                aria-required={required}
                aria-disabled={disabled}
                ref={ref}
            >
                {currentOptions.map((option, index) => {
                    const isSelected = option.value === selectedValue;

                    // We want the selected option to be focusable...
                    const tabIndex = isSelected
                        ? 0
                        : // but we want the first option to be focusable if
                          // no option is selected:
                          !selectedValue && index === 0
                          ? 0
                          : -1;

                    return (
                        <Clickable
                            key={option.value}
                            disabled={disabled}
                            role="radio"
                            aria-checked={isSelected}
                            aria-details={option.tooltip?.()}
                            onClick={() => selectOption(index)}
                            onKeyDown={handleKeyDown}
                            tabIndex={tabIndex}
                            style={[
                                styles.option,
                                disabled && styles.disabled,
                                isSelected && styles.selected,
                            ]}
                        >
                            {() => (
                                <View style={styles.inner}>
                                    <View style={styles.labelRow}>
                                        <LabelMedium>
                                            {option.label()}
                                        </LabelMedium>
                                        {option.tooltip && (
                                            <Tooltip content={option.tooltip()}>
                                                <PhosphorIcon
                                                    icon={infoIcon}
                                                    // eslint-disable-next-line @khanacademy/ts-no-error-suppressions -- see below
                                                    // @ts-expect-error this prop works, but isn't listed in props
                                                    tabIndex={-1}
                                                />
                                            </Tooltip>
                                        )}
                                    </View>
                                    {option.description && (
                                        <LabelSmall
                                            style={styles.optionDescription}
                                        >
                                            {option.description()}
                                        </LabelSmall>
                                    )}
                                </View>
                            )}
                        </Clickable>
                    );
                })}
            </View>
        </RequestedInputLabel>
    );
};

const styles = StyleSheet.create({
    container: {
        display: "grid",
        gap: sizing.size_120,
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    },
    option: {
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        padding: sizing.size_120,
        borderRadius: border.radius.radius_040,
        textAlign: "start",
        display: "flex",
    },
    inner: {
        width: "100%",
    },
    selected: {
        border: `1px solid ${semanticColor.core.border.neutral.default}`,
        background: semanticColor.core.background.instructive.subtle,
        color: semanticColor.core.foreground.instructive.default,
    },
    disabled: {
        border: `1px solid ${semanticColor.core.border.disabled.default}`,
        background: semanticColor.core.background.disabled.default,
        color: semanticColor.core.foreground.disabled.default,
    },
    labelRow: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
    },
    optionDescription: {
        marginBlockStart: sizing.size_020,
        marginBlockEnd: sizing.size_320,
    },
});
